/**
 * RoomEvents - Room and Lobby Socket Event Handlers
 *
 * Handles events related to room state, matchmaking, and lobby UI:
 * - roomStatus: Room state updates
 * - unreadyConfirmed: Player unready confirmation
 * - readyConfirmed: Player ready confirmation
 * - gameStarting: Countdown start
 * - countdownCancelled: Countdown cancelled
 * - gameStarted: Game has started
 */

import { resolveSound } from '../../../shared/resolveSound.js';
import { ClientLogger } from '../../logger/ClientLogger.js';

const roomEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const roomEventsLogger = ClientLogger.getLogger('network') || roomEventsLoggerFallback;

// Countdown ticking starts once the displayed seconds reach this value (3, 2, 1);
// the "0" moment plays the buzzer instead, from the gameStarted handler.
const COUNTDOWN_SOUND_FROM_SECONDS = 3;

/**
 * Play one of the config-driven `general.sounds` entries (round_winner,
 * bullet_wall_hit, countdown_tick, countdown_buzzer, ...). Falls back to a
 * default path/volume when gameConfig hasn't loaded or the key is missing.
 */
const activeGeneralSounds = [];

function playGeneralSound(gameState, key, fallbackPath, fallbackVolume) {
  const resolved = resolveSound(gameState.gameConfig?.general?.sounds?.[key]);
  const path = resolved?.path || fallbackPath;
  const volume = resolved ? resolved.volume : fallbackVolume;
  try {
    const sound = new Audio(path);
    sound.volume = volume;
    activeGeneralSounds.push(sound);
    sound.play().catch((err) => roomEventsLogger.warn(`Failed to play ${key} sound:`, err));
  } catch (err) {
    roomEventsLogger.warn(`Failed to play ${key} sound:`, err);
  }
}

function stopGeneralSounds() {
  for (const sound of activeGeneralSounds) {
    try {
      sound.pause();
      sound.currentTime = 0;
    } catch (err) {
      // Already stopped
    }
  }
  activeGeneralSounds.length = 0;
}

function playCountdownTickSound(gameState) {
  playGeneralSound(gameState, 'countdown_tick', 'assets/sound/general/countdown_tick.mp3', 0.6);
}

function playCountdownBuzzerSound(gameState) {
  playGeneralSound(gameState, 'countdown_buzzer', 'assets/sound/general/countdown_buzzer.mp3', 0.7);
}

const RoomEvents = {
  /**
   * Register all room-related event handlers
   * @param {Socket} socket - Socket.io socket instance
   * @param {GameState} gameState - Game state container
   * @param {Object} callbacks - UI callback functions
   * @param {Function} callbacks.updateLobbyUI - Update lobby UI display
   * @param {Function} callbacks.showCountdownMessage - Show countdown
   * @param {Function} callbacks.updateCountdown - Update countdown display (server-driven ticks)
   * @param {Function} callbacks.hideCountdownMessage - Hide countdown
   * @param {Function} callbacks.setLobbyInputsLocked - Lock/unlock the name input and Locker button (6A)
   * @param {Function} callbacks.teardownToLobby - Local return-to-lobby teardown (game state, renderer,
   *   input, sounds, splash reset + show) run on unready, before disconnecting
   * @param {Function} callbacks.stopWarmupSounds - Stop in-flight warm-up audio at match start
   *   (player/spatial/weapon HTML sounds). Called before the countdown buzzer.
   */
  register(socket, gameState, callbacks) {
    const {
      updateLobbyUI,
      showCountdownMessage,
      updateCountdown,
      hideCountdownMessage,
      suppressReconnectNotification,
      setLobbyInputsLocked = () => {},
      reloadMap = null,
      teardownToLobby = () => {},
      stopWarmupSounds = () => {}
    } = callbacks;

    // Room status updates
    socket.on('roomStatus', (data) => {
      gameState.currentRoomId = data.roomId;
      gameState.roomState = data;

      // If map path is provided and different from current, reload map
      if (data.mapPath && data.mapPath !== gameState.currentMapPath) {
        roomEventsLogger.info('[RoomEvents] Map change detected', { 
          oldMap: gameState.currentMapPath, 
          newMap: data.mapPath 
        });
        gameState.currentMapPath = data.mapPath;
        
        // Trigger map reload (handled by client init system)
        if (typeof reloadMap === 'function') {
          reloadMap(data.mapPath);
        }
      }

      // Update lobby UI if in waiting/countdown state
      if (updateLobbyUI) {
        updateLobbyUI(data);
      }
    });

    // Unready confirmation
    socket.on('unreadyConfirmed', () => {
      // Players spawn into warm-up at join, so by now the splash screen is hidden
      // and a local game is running. Tear it all down to the lobby (this also
      // resets the Ready/Unready buttons, lobby status and warm-up overlay)
      // before disconnecting, so there is never a frozen frame with no way back.
      teardownToLobby();

      // Clear room state - player fully left room on unready
      if (typeof suppressReconnectNotification === 'function') {
        suppressReconnectNotification();
      }
      gameState.currentRoomId = null;

      // Full disconnect like page refresh
      roomEventsLogger.debug('[RoomEvents] unreadyConfirmed: disconnecting socket');
      socket.disconnect();
    });

    // Ready confirmation (after unreadying and readying again)
    socket.on('readyConfirmed', () => {
      const readyButton = document.getElementById('readyButton');
      const unreadyButton = document.getElementById('unreadyButton');

      // Show unready button, hide ready button
      if (readyButton) {
        readyButton.style.display = 'none';
      }

      if (unreadyButton) {
        unreadyButton.style.display = 'inline-block';
        unreadyButton.disabled = false;
        unreadyButton.textContent = 'Unready';
      }

      setLobbyInputsLocked(true);
    });

    // Game starting countdown (initial event)
    socket.on('gameStarting', (data) => {
      if (showCountdownMessage) {
        showCountdownMessage(data.countdownSeconds);
      }
      // Covers short/late-joiner countdowns whose first (and only) event already
      // lands at 3 seconds or below.
      if (data.countdownSeconds > 0 && data.countdownSeconds <= COUNTDOWN_SOUND_FROM_SECONDS) {
        playCountdownTickSound(gameState);
      }
    });

    // Server-driven countdown ticks (keeps all clients synchronized)
    socket.on('countdownTick', (data) => {
      if (updateCountdown) {
        updateCountdown(data.countdownSeconds);
      }
      if (data.countdownSeconds > 0 && data.countdownSeconds <= COUNTDOWN_SOUND_FROM_SECONDS) {
        playCountdownTickSound(gameState);
      }
    });

    // Countdown cancelled
    socket.on('countdownCancelled', () => {
      if (hideCountdownMessage) {
        hideCountdownMessage();
      }
    });

    // Game started - spawn will come next
    socket.on('gameStarted', (data) => {
      gameState.currentRoomId = data.roomId;

      // Warm-up numbers (shown for practice feedback, never persisted server-side —
      // see CombatSystem's lite-mode gate) must not carry into the real match's
      // displayed totals.
      const localPlayer = gameState.players.get(gameState.localId);
      if (localPlayer?.stats) {
        localPlayer.stats = { kills: 0, deaths: 0, damageDealt: 0, damageTaken: 0, score: 0 };
      }

      // Cut leftover warm-up audio (reloads, footsteps, abilities, …) before
      // the buzzer so the round starts clean. The buzzer itself is played
      // after this, and anything triggered post-start is unaffected.
      stopGeneralSounds();
      stopWarmupSounds();

      // The "0" moment - the server emits no countdownTick for 0, so the buzzer
      // fires here instead.
      playCountdownBuzzerSound(gameState);
    });
  }
};

// Export for use in other modules
// For now, RoomEvents is a global object loaded via script tag

export { roomEventsLoggerFallback, roomEventsLogger, RoomEvents };
